import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import qs from 'qs';
import styled from '@emotion/styled';

import AuthWrapper from './common/AuthWrapper';
import InputWrapper from '../common/InputWrapper';
import Checkbox from '../common/Checkbox';
import ErrorBlock from '../common/ErrorBlock';
import Button from '../common/Button';

import { fetchSignUp, fetchActivateAccount, fetchGetBitrixLeedAuth } from '../../actions/auth';

import { preventSubmittingByEnter } from '../../helpers';
import { DOMAIN } from '../../config';
import TagManager from 'react-gtm-module';

import { useTranslation } from 'react-i18next';
import { loadNamespace } from '../../i18n';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

/*import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';*/

const Wrapper = styled('div')`
  .react-tel-input {
    position: relative;
    .special-label {
      display: none;
    }
    .form-control {
      padding-left: 0.9rem;
      position: absolute;
      top: 0;
      left: 0;
      border: none;
      height: calc(1.5em + 0.7rem + 2px);
    }
  }
`;

const phoneRegExp =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const SignUpPage = () => {
  const { t, i18n } = useAutoTranslation();

  const ofertaLabel = (
    <span>
      {t('Я принимаю условия')}{' '}
      <a
        href="https://wiki.kak2c.ru/books/oferty-i-uslugi/page/oferta"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('Пользовательского соглашения')}
      </a>
      , {t('а также я ознакомлен и даю свое')}{' '}
      <a href="https://kak2c.ru/user-agreement" target="_blank" rel="noopener noreferrer">
        {t('Согласие')}
      </a>{' '}
      {t('на сбор и обработку моих персональных данных в соответствии с')}{' '}
      <a href="https://kak2c.ru/privacy-policy" target="_blank" rel="noopener noreferrer">
        {t('Политикой обработки персональных данных')}
      </a>{' '}
    </span>
  );

  const mailingLabel = (
    <span>{t('Я даю свое согласие на получение информационных и рекламных материалов')}</span>
  );

  const validationSchema = Yup.object().shape({
    email: Yup.string().trim().required(t('Необходимое поле')).email(t('Невалидный email')),
    phone: Yup.string()
      .trim()
      .required(t('Необходимое поле'))
      .matches(phoneRegExp, t('Невалидный номер телефона'))
      .test(
        'russianPhone',
        t('Невалидный номер телефона'),
        (t) => t && (t[0] !== '7' || t.length === 11),
      ),
    oferta: Yup.bool().oneOf([true], t('Необходимо принять условия соглашения')),
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const signUp = useSelector((state) => state.auth.signUp);
  const [loading, setLoading] = useState(false);

  const isProd = DOMAIN === 'app-cn.kak2c.ru';

  const addGoogleAnalyticsScriptToHead = () => {
    const lib = document.createElement('script');
    lib.type = 'text/javascript';
    lib.setAttribute('id', 'lib');
    lib.async = true;
    lib.src = 'https://www.googletagmanager.com/gtag/js?id=Lead_from_LK';
    document.head.appendChild(lib);

    const gtag = document.createElement('script');
    gtag.type = 'text/javascript';
    gtag.setAttribute('id', 'gtag');
    gtag.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      dataLayer.push(arguments);
    }

    try {
      gtag('event', 'Lead_from_LK', {});
      console.info('gtag() works!');
    } catch (error) {
      console.error('gtag() doesnt work');
    }`;
    document.head.appendChild(gtag);
  };

  const removeGoogleAnalyticsScriptFromHead = () => {
    const lib = document.getElementById('lib');
    const gtag = document.getElementById('gtag');
    lib.remove();
    gtag.remove();
  };

  useEffect(() => {
    addGoogleAnalyticsScriptToHead();
    document.title = t('Кактус — Регистрация');
    return () => removeGoogleAnalyticsScriptFromHead();
  }, []);

  const parseSearchStr = (str) => {
    if (!str) return {};

    let result = qs.parse(str.slice(1));
    return {
      utm_source: result.utm_source,
      utm_medium: result.utm_medium,
      source_id: result.source_id,
      utm_campaign: result.utm_campaign,
      utm_content: result.utm_content,
    };
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      promocode: '',
      oferta: false,
      mailing: false,
    },
    onSubmit: async (values) => {
      setLoading(true);
      const { name, email, phone } = values;
      if (isProd) {
        const paramsObj = parseSearchStr(history?.location?.search);
        await dispatch(
          fetchGetBitrixLeedAuth({
            title: `Регистрация в ЛК: ${values.phone}`,
            name: values.name,
            email: values.email,
            phone: values.phone,
            promocode: values.promocode,
            sourceId: paramsObj.source_id,
            UTMSource: paramsObj.utm_source,
            UTMMedium: paramsObj.utm_medium,
            UTMCampaign: paramsObj.utm_campaign,
            UTMContent: paramsObj.utm_content,
          }),
        );

        TagManager.dataLayer({
          dataLayer: {
            event: 'Lead',
            eventCategory: 'Lead',
            eventAction: 'Webform',
            eventLabel: 'Complete',
          },
        });
        if (window['ym']) {
          window['ym'](46330035, 'reachGoal', 'Lead');
        }
      }
      const signUpResult = await dispatch(fetchSignUp({ account: { name, email, phone } }));
      await dispatch(fetchActivateAccount(signUpResult.authToken));
      setLoading(false);
    },
    validationSchema,
  });

  const { values, errors, submitCount, handleChange, handleSubmit, setFieldValue } = formik;

  /*const gtmParams = { id: 'GTM-53MM47D' };*/
  const tagManagerArgs = {
    gtmId: 'GTM-53MM47D',
  };
  if (isProd) {
    TagManager.initialize(tagManagerArgs);
  }

  return (
    /*<GTMProvider state={gtmParams}>*/
    <Wrapper>
      <AuthWrapper>
        <h4 style={{ textAlign: 'center', marginBottom: '25px' }}>
          {t('Регистрация нового пользователя')}
        </h4>
        <form onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <InputWrapper
              type="text"
              label={t('Имя')}
              id="name"
              value={values.name.trim()}
              error={errors.name}
              showValidation={submitCount}
              onChange={handleChange}
              placeholder={t('Введите имя')}
            />
          </div>
          <div className="form-group mb-3">
            <InputWrapper
              type="text"
              label={t('Email')}
              id="email"
              value={values.email.trim()}
              error={errors.email}
              showValidation={submitCount}
              onChange={handleChange}
              placeholder={t('Введите email')}
              testId="signUpPageEmailInput"
            />
          </div>
          <div className="form-group mb-3">
            <InputWrapper
              type="text"
              label={t('Номер телефона')}
              id="phone"
              value={values.phone}
              error={errors.phone}
              showValidation={submitCount}
              onChange={(value) => setFieldValue('phone', value)}
              placeholder={t('Номер телефона')}
              isPhoneInput
              testId="signUpPagePhoneInput"
            />
          </div>
          <div className="form-group mb-3">
            <InputWrapper
              type="text"
              label={t('Промокод')}
              id="promocode"
              value={values.promocode}
              onChange={handleChange}
              placeholder={t('Введите промокод')}
            />
          </div>
          <div className="form-group mb-3">
            <Checkbox
              label={ofertaLabel}
              id="oferta"
              checked={values.oferta}
              error={errors.oferta}
              showValidation={submitCount}
              labelStyle={{ fontWeight: '400' }}
              onChange={handleChange}
              testId="signUpPageOfertaCheckbox"
            />
          </div>
          <div className="form-group mb-3">
            <Checkbox
              label={mailingLabel}
              id="mailing"
              checked={values.mailing}
              error={errors.mailing}
              showValidation={submitCount}
              labelStyle={{ fontWeight: '400' }}
              onChange={handleChange}
              testId="signUpPageMailingCheckbox"
            />
          </div>
          {signUp.error && <ErrorBlock error={signUp.error} />}
          <div className="form-group mb-0 text-center">
            <Button
              type="submit"
              text={t('Регистрация')}
              loading={loading}
              disabled={loading}
              style={{ width: '100%' }}
              testId="signUpPageSubmitButton"
            />
          </div>
        </form>
        <div className="text-center" style={{ marginTop: '15px' }}>
          <p>
            <Link to="/forgot" className="ml-1">
              {t('Забыли пароль')}?
            </Link>
          </p>
          <p className="text-50">
            {t('Уже есть аккаунт?')}{' '}
            <Link to="/sign_in" className="ml-1">
              {t('Войти')}
            </Link>
          </p>
        </div>
      </AuthWrapper>
    </Wrapper>
    /*</GTMProvider>*/
  );
};

export default SignUpPage;
